/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { projectDailySales } from './projection';
import { KpiCard, DateStepper, ChartCard } from './components/systemKit';
import { navigate } from './lib/navigate';

// Prime Cost dashboard. Pulls sales + food + labor for the selected week and shows the
// four KPIs that GMs actually watch, plus a small trend chart across the last 8 weeks.
// The date window mirrors food_cost_settings.week_start_day so a restaurant on a Sun-Sat
// week lines up with their reporting cadence.

const DAY_OF_WEEK_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function fmtDate(d) { return d.toISOString().split('T')[0]; }
function fmt$(n) { return `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`; }
function fmtPct(n) { return n != null && isFinite(n) ? `${n.toFixed(1)}%` : '\u2014'; }

function getWeekDates(offset = 0, weekStartDay = 0) {
  const now = new Date();
  const today = now.getDay();
  const backToStart = (today - weekStartDay + 7) % 7;
  const start = new Date(now);
  start.setDate(now.getDate() - backToStart + offset * 7);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

// Returns the KpiCard valueTone for a percent-vs-target KPI. Target thresholds:
//   under target-3  → 'success'  (comfortably under budget)
//   within  ±3      → 'warning'  (approaching the line)
//   over   target+3 → 'critical' (over budget)
function kpiValueTone(pct, target) {
  if (pct == null) return 'neutral';
  if (pct <= target - 3) return 'success';
  if (pct <= target + 3) return 'warning';
  return 'critical';
}

// onNavigate prop removed — Operations is no longer a switchboard. Cross-tab
// (and cross-page) navigation flows through src/lib/navigate.js. The
// QuickAction "Log food invoice" was previously a same-page setTab call;
// it now crosses to the Inventory group.
export default function OperationsOverview({ companyId }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekStartDay, setWeekStartDay] = useState(0);
  const [targets, setTargets] = useState({ food: 30, labor: 25, prime: 60 });
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState(0);
  const [actualSales, setActualSales] = useState(0);
  const [foodTotal, setFoodTotal] = useState(0);
  const [laborTotal, setLaborTotal] = useState(0);
  const [trend, setTrend] = useState([]); // [{ label, sales, food, labor, foodPct, laborPct, primePct }]
  // Daily projection rows over a 28-day window (14 past + 14 future). Past days carry actual
  // sales bars; every day carries projected sales (DOW history avg) + projected labor (scheduled).
  const [projection, setProjection] = useState([]);

  const weekDates = getWeekDates(weekOffset, weekStartDay);
  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const weekStart = fmtDate(weekDates[0]);
  const weekEnd = fmtDate(weekDates[6]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data } = await supabase
        .from('food_cost_settings')
        .select('target_food_cost_pct, target_prime_cost_pct, week_start_day')
        .eq('company_id', companyId)
        .maybeSingle();
      if (data) {
        const food = Number(data.target_food_cost_pct) || 30;
        const prime = Number(data.target_prime_cost_pct) || 60;
        setTargets({ food, labor: Math.max(0, prime - food), prime });
        setWeekStartDay(Number(data.week_start_day) || 0);
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchWindow();
  }, [companyId, weekStart, weekEnd, weekStartDay]);

  async function fetchWindow() {
    setLoading(true);
    // Pull 6 weeks of prior sales too so the DOW projection has data to work with when the
    // current week is only partially entered.
    const histStartDate = new Date(weekStart + 'T00:00:00');
    histStartDate.setDate(histStartDate.getDate() - 42);
    const histStartStr = fmtDate(histStartDate);

    const [salesRes, foodRes, shiftRes, empRes] = await Promise.all([
      supabase.from('labor_sales').select('date, total_sales, projected_sales').eq('company_id', companyId).gte('date', histStartStr).lte('date', weekEnd),
      supabase.from('food_cost_entries').select('date, total').eq('company_id', companyId).gte('date', weekStart).lte('date', weekEnd),
      supabase.from('shifts').select('day, employee_id, start_time, end_time').eq('company_id', companyId).gte('day', weekStart).lte('day', weekEnd),
      supabase.from('employees').select('id, hourly_rate').eq('company_id', companyId),
    ]);
    const salesByDate = {};
    const salesOverrides = {};
    (salesRes.data || []).forEach(r => {
      salesByDate[r.date] = Number(r.total_sales || 0);
      if (r.projected_sales != null) salesOverrides[r.date] = Number(r.projected_sales);
    });
    const totalFood = (foodRes.data || []).reduce((s, r) => s + Number(r.total || 0), 0);
    const empRate = {};
    (empRes.data || []).forEach(e => { empRate[String(e.id)] = Number(e.hourly_rate) || 0; });
    const totalLabor = (shiftRes.data || []).reduce((s, sh) => s + calcShiftCost(sh, empRate), 0);

    // Build the 7 dates of the selected week, then fill gaps with same-DOW projections so
    // partial weeks compare food/labor against a realistic full-week revenue target instead
    // of divide-by-three-days and reporting 300% labor.
    const weekDatesList = [];
    const wd = new Date(weekStart + 'T00:00:00');
    for (let i = 0; i < 7; i++) { weekDatesList.push(fmtDate(wd)); wd.setDate(wd.getDate() + 1); }
    const todayStr = fmtDate(new Date());
    const projMap = projectDailySales(weekDatesList, salesByDate, salesOverrides);
    let actualThisWeek = 0;
    const effectiveSales = weekDatesList.reduce((sum, ds) => {
      const actual = salesByDate[ds];
      if (actual != null && actual > 0) { actualThisWeek += actual; return sum + actual; }
      if (ds < todayStr) return sum;
      return sum + (projMap[ds] || 0);
    }, 0);

    setSales(effectiveSales);
    setActualSales(actualThisWeek);
    setFoodTotal(totalFood);
    setLaborTotal(totalLabor);
    await Promise.all([fetchTrend(), fetchProjection()]);
    setLoading(false);
  }

  // Build a 28-day forecast view: 14 days behind today + 14 ahead.
  // - Actual Sales: labor_sales.total_sales for past dates only.
  // - Projected Sales: average of the same day-of-week from the prior 4 weeks (auto, no entry).
  // - Projected Labor: sum of scheduled hours × hourly_rate per day.
  // We fetch sales 6 weeks back so the DOW projection has enough history to land on something
  // meaningful even when the operator only just started entering numbers.
  async function fetchProjection() {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const winStart = new Date(today); winStart.setDate(today.getDate() - 14);
    const winEnd = new Date(today); winEnd.setDate(today.getDate() + 14);
    const histStart = new Date(winStart); histStart.setDate(winStart.getDate() - 28);

    const [salesRes, shiftRes, empRes] = await Promise.all([
      // Pull projected_sales too so manual overrides set on the Sales tab show up on the chart.
      supabase.from('labor_sales').select('date, total_sales, projected_sales').eq('company_id', companyId).gte('date', fmtDate(histStart)).lte('date', fmtDate(winEnd)),
      supabase.from('shifts').select('day, employee_id, start_time, end_time').eq('company_id', companyId).gte('day', fmtDate(winStart)).lte('day', fmtDate(winEnd)),
      supabase.from('employees').select('id, hourly_rate').eq('company_id', companyId),
    ]);

    const empRate = {};
    (empRes.data || []).forEach(e => { empRate[String(e.id)] = Number(e.hourly_rate) || 0; });
    const salesByDate = {};
    const overrides = {};
    (salesRes.data || []).forEach(r => {
      salesByDate[r.date] = Number(r.total_sales || 0);
      if (r.projected_sales != null) overrides[r.date] = Number(r.projected_sales);
    });
    const laborByDate = {};
    (shiftRes.data || []).forEach(sh => {
      const cost = calcShiftCost(sh, empRate);
      laborByDate[sh.day] = (laborByDate[sh.day] || 0) + cost;
    });

    const todayStr = fmtDate(today);
    // Build the 28-day window first, then run the shared projection helper across all dates.
    // The util handles same-DOW averaging + Phase 2 trend adjustment in one place.
    const dates = [];
    const dayMeta = {};
    for (let i = 0; i < 28; i++) {
      const d = new Date(winStart); d.setDate(winStart.getDate() + i);
      const ds = fmtDate(d);
      dates.push(ds);
      dayMeta[ds] = {
        date: ds,
        weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: d.getDate(),
      };
    }
    const projMap = projectDailySales(dates, salesByDate, overrides);
    const rows = dates.map(ds => {
      const isPast = ds < todayStr;
      return {
        ...dayMeta[ds],
        actualSales: isPast && salesByDate[ds] != null ? salesByDate[ds] : null,
        projectedSales: projMap[ds],
        projectedLabor: laborByDate[ds] != null ? laborByDate[ds] : null,
        isPast,
        isToday: ds === todayStr,
      };
    });
    setProjection(rows);
  }

  async function fetchTrend() {
    const WEEKS = 8;
    const weeks = [];
    for (let i = WEEKS - 1; i >= 0; i--) {
      const wd = getWeekDates(-i, weekStartDay);
      weeks.push({ start: fmtDate(wd[0]), end: fmtDate(wd[6]), dates: wd.map(fmtDate), label: wd[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
    }
    const rangeStart = weeks[0].start;
    const rangeEnd = weeks[WEEKS - 1].end;
    // Pull an extra 6 weeks of history so the day-of-week projection has enough to work with
    // when we need to fill a partial current week.
    const histStartDate = new Date(rangeStart + 'T00:00:00');
    histStartDate.setDate(histStartDate.getDate() - 42);
    const histStartStr = fmtDate(histStartDate);

    const [salesRes, foodRes, shiftRes, empRes] = await Promise.all([
      supabase.from('labor_sales').select('date, total_sales, projected_sales').eq('company_id', companyId).gte('date', histStartStr).lte('date', rangeEnd),
      supabase.from('food_cost_entries').select('date, total').eq('company_id', companyId).gte('date', rangeStart).lte('date', rangeEnd),
      supabase.from('shifts').select('day, employee_id, start_time, end_time').eq('company_id', companyId).gte('day', rangeStart).lte('day', rangeEnd),
      supabase.from('employees').select('id, hourly_rate').eq('company_id', companyId),
    ]);
    const empRate = {};
    (empRes.data || []).forEach(e => { empRate[String(e.id)] = Number(e.hourly_rate) || 0; });
    const salesByDate = {};
    const salesOverrides = {};
    (salesRes.data || []).forEach(r => {
      salesByDate[r.date] = Number(r.total_sales || 0);
      if (r.projected_sales != null) salesOverrides[r.date] = Number(r.projected_sales);
    });
    const allFood = foodRes.data || [];
    const allShifts = shiftRes.data || [];
    const todayStr = fmtDate(new Date());

    const rows = weeks.map(w => {
      // Real sales for days entered, DOW projection for today + future, zero
      // for missing past days. Two parallel sums:
      //   - `effectiveSales` — projection-padded for display
      //   - `actualThisWeek` — only days with actual > 0
      // Plus `actualDays` — count of days with actual > 0. Drives the
      // completeness gate below.
      const projMap = projectDailySales(w.dates, salesByDate, salesOverrides);
      let actualThisWeek = 0;
      let actualDays = 0;
      const effectiveSales = w.dates.reduce((sum, ds) => {
        const actual = salesByDate[ds];
        if (actual != null && actual > 0) { actualThisWeek += actual; actualDays++; return sum + actual; }
        if (ds < todayStr) return sum;
        return sum + (projMap[ds] || 0);
      }, 0);
      const foodTotalW = allFood.filter(r => r.date >= w.start && r.date <= w.end).reduce((s, r) => s + Number(r.total || 0), 0);
      const laborTotalW = allShifts.filter(r => r.day >= w.start && r.day <= w.end).reduce((s, sh) => s + calcShiftCost(sh, empRate), 0);
      // Data-completeness gate: a week needs a majority of days with actual
      // entered sales (≥4 of 7) before we compute %-of-sales. Avoids the
      // "operator entered Sunday, Monday-Saturday are zeros" artifact where
      // labor covers 7 days but the denominator only covers 1 day —
      // producing 300%+ phantom spikes. See DESIGN_SYSTEM.md §Charts handle
      // gap weeks by rendering null, not zero.
      const hasCompleteData = actualDays >= 4 && actualThisWeek > 0;
      const foodPct = hasCompleteData ? (foodTotalW / actualThisWeek) * 100 : null;
      const laborPct = hasCompleteData ? (laborTotalW / actualThisWeek) * 100 : null;
      const primePct = (foodPct != null && laborPct != null) ? foodPct + laborPct : null;
      return { label: w.label, sales: effectiveSales, actualSales: actualThisWeek, actualDays, food: foodTotalW, labor: laborTotalW, foodPct, laborPct, primePct };
    });
    setTrend(rows);
  }

  const foodPct = sales > 0 ? (foodTotal / sales) * 100 : null;
  const laborPct = sales > 0 ? (laborTotal / sales) * 100 : null;
  const primePct = (foodPct != null && laborPct != null) ? foodPct + laborPct : null;

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Operations</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Prime cost at a glance — {DAY_OF_WEEK_LABELS[weekStartDay]} to {DAY_OF_WEEK_LABELS[(weekStartDay + 6) % 7]}</p>
          </div>
          <DateStepper
            label={weekLabel}
            onPrev={() => setWeekOffset(w => w - 1)}
            onNext={() => setWeekOffset(w => w + 1)}
            onReset={weekOffset !== 0 ? () => setWeekOffset(0) : undefined}
            resetLabel="This week"
          />
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="Sales"
            value={sales > 0 ? fmt$(sales) : '\u2014'}
            secondary={sales > 0
              ? (actualSales > 0 && actualSales < sales
                  ? `${fmt$(actualSales)} actual + projection`
                  : (actualSales >= sales ? 'this week' : 'projected this week'))
              : 'No sales logged'}
            onClick={() => navigate('operations', 'sales')}
          />
          <KpiCard
            label="Food Cost"
            value={foodPct != null ? fmtPct(foodPct) : '\u2014'}
            valueTone={kpiValueTone(foodPct, targets.food)}
            secondary={foodTotal > 0 ? `${fmt$(foodTotal)} (target ${targets.food}%)` : 'Add invoices'}
            onClick={() => navigate('operations', 'food')}
          />
          <KpiCard
            label="Labor Cost"
            value={laborPct != null ? fmtPct(laborPct) : '\u2014'}
            valueTone={kpiValueTone(laborPct, targets.labor)}
            secondary={laborTotal > 0 ? `${fmt$(laborTotal)} (target ${targets.labor}%)` : 'Schedule shifts'}
            onClick={() => navigate('operations', 'labor')}
          />
          <KpiCard
            label="Prime Cost"
            value={primePct != null ? fmtPct(primePct) : '\u2014'}
            valueTone={kpiValueTone(primePct, targets.prime)}
            secondary={primePct != null ? `food + labor (target ${targets.prime}%)` : 'Need sales + costs'}
          />
        </div>

        {/* Sales & labor projection — daily bars (actuals) + lines (projected) over a 28-day window.
            Bar/line colors are DATA COLORS and stay hardcoded per DESIGN_SYSTEM.md §Data Colors. */}
        <div className="mb-6">
          <ChartCard
            title="Sales & labor — actuals + projection"
            subtitle="14 days back, 14 days ahead. Projected sales = avg of same weekday from the last 4 weeks."
            legend={
              <>
                <LegendSwatch color="#FB923C" label="Actual sales" kind="bar" />
                <LegendSwatch color="#C2410C" label="Projected sales" kind="line" />
                <LegendSwatch color="#2563EB" label="Projected labor" kind="line dashed" />
              </>
            }
          >
            {projection.length > 0 ? (
              <ProjectionChart rows={projection} />
            ) : (
              <div className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</div>
            )}
          </ChartCard>
        </div>

        {/* Trend */}
        <div className="mb-6">
          <ChartCard
            title="Last 8 weeks"
            legend={
              <>
                <LegendDot color="#2D8F5E" label="Food %" />
                <LegendDot color="#D97706" label="Labor %" />
                <LegendDot color="#4F46E5" label="Prime %" />
              </>
            }
          >
            {trend.filter(w => w.primePct != null).length < 2 ? (
              <div className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Enter sales + costs for a couple of weeks to see trends.</div>
            ) : (
              <TrendChart rows={trend} targets={targets} />
            )}
          </ChartCard>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <QuickAction label="Enter sales" onClick={() => navigate('operations', 'sales')} />
          {/* Log food invoice — Price Tracker now lives under the Inventory group. */}
          <QuickAction label="Log food invoice" onClick={() => navigate('inventory', 'pricetracker')} />
          <QuickAction label="Review labor" onClick={() => navigate('operations', 'labor')} />
        </div>

        {loading && <div className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>Loading…</div>}
      </div>
    </div>
  );
}

function QuickAction({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded-lg p-5 transition-colors flex items-center justify-between"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', cursor: 'pointer' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
    >
      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)' }}><path d="M5 12h14M13 5l7 7-7 7"/></svg>
    </button>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

function TrendChart({ rows, targets }) {
  // Simple SVG line chart for the three %-of-sales series. Responsive via viewBox; no
  // external chart lib so bundle stays small and the look matches the Trend chart elsewhere.
  const W = 640;
  const H = 200;
  const padL = 32, padR = 12, padT = 10, padB = 24;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const allPcts = rows.flatMap(r => [r.foodPct, r.laborPct, r.primePct]).filter(v => v != null);
  const maxY = Math.max(80, Math.ceil(Math.max(...allPcts, targets.prime + 10) / 10) * 10);
  const minY = 0;
  const xAt = (i) => padL + (rows.length <= 1 ? plotW / 2 : (i / (rows.length - 1)) * plotW);
  const yAt = (v) => padT + plotH - ((v - minY) / (maxY - minY)) * plotH;

  // M for first non-null point, L for the rest. Keying off i===0 broke whenever early
  // weeks had no data — SVG silently refused to draw a path that started with L.
  const pathFor = (key) => {
    let started = false;
    return rows.map((r, i) => {
      if (r[key] == null) return '';
      const cmd = started ? 'L' : 'M';
      started = true;
      return `${cmd}${xAt(i).toFixed(1)},${yAt(r[key]).toFixed(1)}`;
    }).filter(Boolean).join(' ');
  };

  const yTicks = [];
  for (let v = 0; v <= maxY; v += 20) yTicks.push(v);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="200" preserveAspectRatio="none">
      {yTicks.map(v => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yAt(v)} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" />
          <text x={padL - 6} y={yAt(v) + 3} fontSize="9" fill="var(--text-muted)" textAnchor="end">{v}%</text>
        </g>
      ))}
      {/* Gap markers — subtle dashed verticals at weeks with null percentages
          (no sales entered). Operator hover surfaces a "No sales data" hint
          via <title>. See DESIGN_SYSTEM.md §Charts handle gap weeks by
          rendering null, not zero. */}
      {rows.map((r, i) => (
        r.primePct == null ? (
          <line
            key={`gap-${i}`}
            x1={xAt(i)} x2={xAt(i)} y1={padT} y2={padT + plotH}
            stroke="var(--border)" strokeWidth="1" strokeDasharray="2 3" opacity="0.3"
          >
            <title>No sales data entered for this week.</title>
          </line>
        ) : null
      ))}
      {/* Data series colors stay hardcoded per §Data Colors — these encode content, not chrome. */}
      <path d={pathFor('foodPct')} fill="none" stroke="#2D8F5E" strokeWidth="2" />
      <path d={pathFor('laborPct')} fill="none" stroke="#D97706" strokeWidth="2" />
      <path d={pathFor('primePct')} fill="none" stroke="#4F46E5" strokeWidth="2.5" />
      {rows.map((r, i) => (
        <g key={i}>
          {r.foodPct != null && <circle cx={xAt(i)} cy={yAt(r.foodPct)} r="2.5" fill="#2D8F5E" />}
          {r.laborPct != null && <circle cx={xAt(i)} cy={yAt(r.laborPct)} r="2.5" fill="#D97706" />}
          {r.primePct != null && <circle cx={xAt(i)} cy={yAt(r.primePct)} r="3" fill="#4F46E5" />}
          <text x={xAt(i)} y={H - 6} fontSize="9" fill="var(--text-muted)" textAnchor="middle">{r.label}</text>
        </g>
      ))}
    </svg>
  );
}

// Daily bars (actual sales) + two lines (projected sales, projected labor) over a 28-day window.
// Today is marked with a dashed vertical divider so past/future is obvious. Hovering a column
// surfaces a tooltip with the actual numbers — the eye can only approximate from the chart.
function ProjectionChart({ rows }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const W = 720;
  const H = 240;
  const padL = 44, padR = 12, padT = 12, padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = rows.length;
  const colW = plotW / n;
  const barW = Math.max(2, colW * 0.55);

  const allVals = rows.flatMap(r => [r.actualSales, r.projectedSales, r.projectedLabor]).filter(v => v != null && v > 0);
  const rawMax = allVals.length > 0 ? Math.max(...allVals) : 100;
  // Round up to a nice tick so the y-axis labels read clean.
  const niceCeil = (v) => {
    const order = Math.pow(10, Math.floor(Math.log10(v)));
    const n2 = v / order;
    let bucket;
    if (n2 <= 1) bucket = 1; else if (n2 <= 2) bucket = 2; else if (n2 <= 5) bucket = 5; else bucket = 10;
    return bucket * order;
  };
  const maxY = niceCeil(Math.max(rawMax, 100) * 1.1);

  const xCenter = (i) => padL + colW * (i + 0.5);
  const yAt = (v) => padT + plotH - (v / maxY) * plotH;

  // Lines are drawn over the whole window. Use M on first non-null point to avoid the
  // "starts with L" silent-drop bug we hit before.
  const linePath = (key) => {
    let started = false;
    return rows.map((r, i) => {
      if (r[key] == null) return '';
      const cmd = started ? 'L' : 'M';
      started = true;
      return `${cmd}${xCenter(i).toFixed(1)},${yAt(r[key]).toFixed(1)}`;
    }).filter(Boolean).join(' ');
  };

  const yTicks = [0, maxY * 0.25, maxY * 0.5, maxY * 0.75, maxY].map(v => Math.round(v));
  const todayIdx = rows.findIndex(r => r.isToday);

  const fmtMoney = (n2) => {
    if (n2 == null) return '—';
    if (n2 >= 1000) return `$${(n2 / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return `$${Math.round(n2)}`;
  };

  return (
    <div className="relative">
      {/* Every visual layer has pointerEvents="none" so the hit-rects below are the ONLY
         elements that respond to hover. Without this, painted bars/dots/lines intercept the
         cursor (SVG default is visiblePainted) and the column-level hover never fires. */}
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="240" preserveAspectRatio="none">
        {/* Y gridlines + labels — chrome, use CSS vars. */}
        {yTicks.map(v => (
          <g key={v} style={{ pointerEvents: 'none' }}>
            <line x1={padL} x2={W - padR} y1={yAt(v)} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" />
            <text x={padL - 6} y={yAt(v) + 3} fontSize="9" fill="var(--text-muted)" textAnchor="end">{fmtMoney(v)}</text>
          </g>
        ))}

        {/* Today divider — only when today falls inside the window. Chrome. */}
        {todayIdx >= 0 && (
          <line
            x1={padL + colW * todayIdx} x2={padL + colW * todayIdx}
            y1={padT} y2={padT + plotH}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 3"
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Actual sales bars (past only). Semi-transparent so the lines remain readable on top. */}
        {rows.map((r, i) => r.actualSales != null && r.actualSales > 0 ? (
          <rect
            key={`bar-${i}`}
            x={xCenter(i) - barW / 2}
            y={yAt(r.actualSales)}
            width={barW}
            height={Math.max(1, padT + plotH - yAt(r.actualSales))}
            fill="#FB923C"
            opacity="0.85"
            rx="2"
            style={{ pointerEvents: 'none' }}
          />
        ) : null)}

        {/* Projected sales line */}
        <path d={linePath('projectedSales')} fill="none" stroke="#C2410C" strokeWidth="2" style={{ pointerEvents: 'none' }} />
        {rows.map((r, i) => r.projectedSales != null ? (
          <circle key={`ps-${i}`} cx={xCenter(i)} cy={yAt(r.projectedSales)} r="2.5" fill="#C2410C" style={{ pointerEvents: 'none' }} />
        ) : null)}

        {/* Projected labor line — dashed to read as "schedule-derived, not actual" */}
        <path d={linePath('projectedLabor')} fill="none" stroke="#2563EB" strokeWidth="2" strokeDasharray="4 3" style={{ pointerEvents: 'none' }} />
        {rows.map((r, i) => r.projectedLabor != null ? (
          <circle key={`pl-${i}`} cx={xCenter(i)} cy={yAt(r.projectedLabor)} r="2.5" fill="#2563EB" style={{ pointerEvents: 'none' }} />
        ) : null)}

        {/* Day labels — every other day so the axis doesn't crowd. Chrome, use vars. */}
        {rows.map((r, i) => i % 2 === 0 ? (
          <g key={`label-${i}`} style={{ pointerEvents: 'none' }}>
            <text x={xCenter(i)} y={H - 18} fontSize="9" fill={r.isToday ? 'var(--text-primary)' : 'var(--text-muted)'} textAnchor="middle" fontWeight={r.isToday ? '600' : '400'}>{r.weekday}</text>
            <text x={xCenter(i)} y={H - 6} fontSize="9" fill={r.isToday ? 'var(--text-primary)' : 'var(--text-muted)'} textAnchor="middle" fontWeight={r.isToday ? '600' : '400'}>{r.dayNum}</text>
          </g>
        ) : null)}

        {/* Hover hit-areas — full-column rects extending down through the day labels so the
            user can also hover the weekday/date at the bottom to surface the tooltip.
            pointerEvents="all" forces SVG to hit-test transparent fills (default is
            "visiblePainted" which silently drops events on fill='transparent', causing the
            cursor to fall through to lines/circles and triggering tooltip flicker). */}
        {rows.map((r, i) => (
          <rect
            key={`hit-${i}`}
            x={padL + colW * i}
            y={padT}
            width={colW}
            height={H - padT - 2}
            fill="transparent"
            pointerEvents="all"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
            style={{ cursor: 'pointer' }}
          />
        ))}

        {/* Hovered column highlight — pointerEvents none so it never absorbs the cursor.
            Neutral chrome — uses primary text as a column wash, kept at 4% opacity. */}
        {hoverIdx != null && (
          <rect
            x={padL + colW * hoverIdx} y={padT}
            width={colW} height={plotH}
            fill="var(--text-primary)" opacity="0.04"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </svg>

      {/* Tooltip — positioned above the hovered column. Anchored absolutely inside the wrapper.
          Elevated surface per §Shadows — var(--bg-elevated) + var(--border). */}
      {hoverIdx != null && (() => {
        const r = rows[hoverIdx];
        const leftPct = ((padL + colW * hoverIdx + colW / 2) / W) * 100;
        return (
          <div
            className="absolute pointer-events-none rounded-lg px-3 py-2 text-xs"
            style={{
              left: `${leftPct}%`,
              top: 0,
              transform: 'translate(-50%, -8px)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              minWidth: 180,
              zIndex: 10,
            }}
          >
            <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>{r.weekday} · {new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{r.isToday ? ' (today)' : ''}</div>
            <div className="mt-1 space-y-0.5">
              {r.actualSales != null && (
                <div className="flex items-center justify-between gap-3">
                  <span style={{ color: 'var(--text-secondary)' }}>Actual sales</span>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmtMoney(r.actualSales)}</span>
                </div>
              )}
              {r.projectedSales != null && (
                <div className="flex items-center justify-between gap-3">
                  <span style={{ color: 'var(--text-secondary)' }}>Projected sales</span>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmtMoney(r.projectedSales)}</span>
                </div>
              )}
              {r.projectedLabor != null && (
                <div className="flex items-center justify-between gap-3">
                  <span style={{ color: 'var(--text-secondary)' }}>Projected labor</span>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{fmtMoney(r.projectedLabor)}</span>
                </div>
              )}
              {r.actualSales == null && r.projectedSales == null && r.projectedLabor == null && (
                <div style={{ color: 'var(--text-muted)' }}>No data yet</div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Legend swatch for the projection chart — supports bar / line / line-dashed glyphs.
function LegendSwatch({ color, label, kind = 'bar' }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {kind === 'bar' ? (
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      ) : (
        <svg width="14" height="2" viewBox="0 0 14 2">
          <line x1="0" y1="1" x2="14" y2="1" stroke={color} strokeWidth="2" strokeDasharray={kind === 'line dashed' ? '3 2' : ''} />
        </svg>
      )}
      <span>{label}</span>
    </span>
  );
}

// Hours are stored as 24h "HH:MM" strings. Midnight-spanning shifts add 24h.
function calcShiftCost(shift, empRate) {
  const hrs = calcHours(shift.start_time, shift.end_time);
  return hrs * (empRate[String(shift.employee_id)] || 0);
}
function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startH = sh + (sm || 0) / 60;
  let endH = eh + (em || 0) / 60;
  if (endH < startH) endH += 24;
  return Math.max(0, endH - startH);
}
